import { useAuth } from "@clerk/clerk-react";
import { BACKEND_URL } from "@/constants/api";

/**
 * Hook that returns an authenticated fetch wrapper.
 *
 * Usage:
 *   const apiFetch = useApiFetch();
 *   const res = await apiFetch("/api/some-endpoint", { method: "POST", body: ... });
 *
 * Behaviour:
 * - Prepends BACKEND_URL when the URL starts with "/api"
 * - Injects Authorization: Bearer <token> automatically
 * - Emits custom window events for specific HTTP status codes so the global
 *   ApiErrorModal can react:
 *     api-error:429  - rate limited
 *     api-error:403  - forbidden
 *     api-error:500  - server error (500, 502, 503, 504)
 * - Always returns the original Response - never swallows it
 */
export function useApiFetch() {
  const { getToken } = useAuth();

  return async function apiFetch(url, options = {}) {
    const token = await getToken();

    const resolvedUrl = url.startsWith("/api")
      ? `${BACKEND_URL}${url.slice(1)}` // BACKEND_URL ends with "/", strip leading "/"
      : url;

    const headers = {
      ...(options.headers ?? {}),
      Authorization: `Bearer ${token}`,
    };

    const res = await fetch(resolvedUrl, { ...options, headers });

    if (res.status === 429) {
      window.dispatchEvent(new CustomEvent("api-error:429"));
    } else if (res.status === 403) {
      window.dispatchEvent(new CustomEvent("api-error:403"));
    } else if ([500, 502, 503, 504].includes(res.status)) {
      window.dispatchEvent(new CustomEvent("api-error:500"));
    }

    return res;
  };
}
